import { useState, useEffect, useCallback } from 'react';

export const useLocalStorage = (key, initialValue) => {
  // Get value from localStorage or use initial value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when value changes
  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  // Remove from localStorage
  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue];
};

// Hook for managing wheel history
export const useWheelHistory = () => {
  const [history, setHistory] = useLocalStorage('wheel-history', []);

  const addToHistory = useCallback((entry) => {
    const historyEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      result: entry.text,
      timestamp: new Date().toISOString(),
      variant: entry.variant || 'main',
    };
    setHistory(prev => [historyEntry, ...prev.slice(0, 99)]); // Keep last 100
    return historyEntry;
  }, [setHistory]);

  const clearHistory = useCallback(() => {
    setHistory([]);
  }, [setHistory]);

  const removeFromHistory = useCallback((id) => {
    setHistory(prev => prev.filter(h => h.id !== id));
  }, [setHistory]);

  return { history, addToHistory, clearHistory, removeFromHistory };
};

// Hook for managing saved wheels
export const useSavedWheels = () => {
  const [savedWheels, setSavedWheels] = useLocalStorage('saved-wheels', []);

  const saveWheel = useCallback((name, entries, variant = 'main') => {
    const wheel = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      entries,
      variant,
      createdAt: new Date().toISOString(),
    };
    setSavedWheels(prev => [wheel, ...prev]);
    return wheel;
  }, [setSavedWheels]);

  const deleteWheel = useCallback((id) => {
    setSavedWheels(prev => prev.filter(w => w.id !== id));
  }, [setSavedWheels]);

  const updateWheel = useCallback((id, updates) => {
    setSavedWheels(prev => prev.map(w => 
      w.id === id ? { ...w, ...updates } : w
    ));
  }, [setSavedWheels]);

  return { savedWheels, saveWheel, deleteWheel, updateWheel };
};
